import React, { useState, useEffect, useCallback } from "react";
import { getAdminGLAccounts, updateGLAccount } from "../../adminApi";
import type { GLAccount } from "../../adminTypes";

/**
 * AIGLList — Admin tab for managing the curated GL account list used by
 * the AI coding assistant (Tier 4 in cc_resolver.py).
 *
 * Only GL accounts with ai_coding_enabled = TRUE are sent to Claude when
 * resolving unmatched credit card transactions.  A smaller, focused list
 * produces more accurate results than the full chart of accounts.
 *
 * UI: two sections
 *   Top — "AI Coding List": GLs already enabled, each with a Remove button.
 *   Bottom — "Add GL Accounts": searchable list of remaining active GLs,
 *            each with an Add button.
 */
const AIGLList: React.FC = () => {
  const [accounts, setAccounts] = useState<GLAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [toggling, setToggling] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAdminGLAccounts();
      setAccounts(data);
      setError("");
    } catch (err: any) {
      setError(err?.response?.data?.error || "Failed to load GL accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleToggle = async (acc: GLAccount, enable: boolean) => {
    setToggling(acc.id);
    try {
      await updateGLAccount(acc.id, { ai_coding_enabled: enable });
      setAccounts((prev) =>
        prev.map((a) => (a.id === acc.id ? { ...a, ai_coding_enabled: enable } : a))
      );
      setToast({
        type: "success",
        message: enable
          ? `${acc.gl_number} added to AI coding list`
          : `${acc.gl_number} removed from AI coding list`,
      });
    } catch (err: any) {
      setToast({ type: "error", message: err?.response?.data?.error || "Failed to update" });
    } finally {
      setToggling(null);
    }
  };

  // Partition accounts
  const aiList = accounts.filter((a) => a.active && a.ai_coding_enabled);
  const available = accounts.filter(
    (a) =>
      a.active &&
      !a.ai_coding_enabled &&
      (!search ||
        a.gl_number.toLowerCase().includes(search.toLowerCase()) ||
        a.description.toLowerCase().includes(search.toLowerCase()) ||
        (a.category || "").toLowerCase().includes(search.toLowerCase()))
  );

  // ── Styles ──────────────────────────────────────────────────────────────────
  const toastStyle = (type: string): React.CSSProperties => ({
    position: "fixed",
    top: 16,
    right: 16,
    padding: "12px 20px",
    borderRadius: 6,
    color: "white",
    fontWeight: 500,
    zIndex: 9999,
    background: type === "success" ? "#2d7a2d" : "#E33126",
  });

  const sectionHeader: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 700,
    color: "#00386C",
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
    gap: 10,
  };

  const countBadge = (n: number, color = "#00386C", bg = "#e0ecf5"): React.CSSProperties => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 700,
    color,
    background: bg,
  });

  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  };

  const thStyle: React.CSSProperties = {
    background: "#00386C",
    color: "white",
    padding: "9px 12px",
    textAlign: "left",
    fontWeight: 600,
    fontSize: 13,
  };

  const tdStyle: React.CSSProperties = {
    padding: "9px 12px",
    borderBottom: "1px solid #eee",
    verticalAlign: "middle",
  };

  const removeBtn = (disabled: boolean): React.CSSProperties => ({
    padding: "4px 14px",
    background: disabled ? "#fef2f2" : "#fff0ee",
    color: disabled ? "#ccc" : "#c0392b",
    border: `1px solid ${disabled ? "#fca5a5" : "#fca5a5"}`,
    borderRadius: 4,
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 600,
    fontSize: 13,
    opacity: disabled ? 0.6 : 1,
  });

  const addBtn = (disabled: boolean): React.CSSProperties => ({
    padding: "4px 14px",
    background: disabled ? "#f0f4ff" : "#e8f0fe",
    color: disabled ? "#aaa" : "#00386C",
    border: "1px solid #b8d0f0",
    borderRadius: 4,
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 600,
    fontSize: 13,
    opacity: disabled ? 0.6 : 1,
  });

  const monoStyle: React.CSSProperties = {
    fontFamily: "monospace",
    fontWeight: 600,
  };

  const catBadge = (cat: string | null): React.ReactNode => {
    if (!cat) return <span style={{ color: "#bbb" }}>—</span>;
    return (
      <span style={{
        display: "inline-block", padding: "2px 8px", borderRadius: 10,
        fontSize: 11, fontWeight: 600, color: "#00386C", background: "#e0ecf5",
      }}>
        {cat}
      </span>
    );
  };

  if (loading) return <div style={{ padding: 40, color: "#666", textAlign: "center" }}>Loading GL accounts...</div>;
  if (error) return <div style={{ padding: 40, color: "#E33126", textAlign: "center" }}>{error}</div>;

  return (
    <div style={{ padding: 0 }}>
      {toast && <div style={toastStyle(toast.type)}>{toast.message}</div>}

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, color: "#00386C", margin: "0 0 6px 0" }}>
          AI Coding GL List
        </h3>
        <p style={{ fontSize: 13, color: "#666", margin: 0 }}>
          Control which GL accounts the AI assistant can choose from when coding unmatched
          credit card transactions. A focused list produces more accurate results.
          {aiList.length === 0 && (
            <strong style={{ color: "#c0392b" }}>
              {" "}No accounts added yet — AI will fall back to all active GL accounts.
            </strong>
          )}
        </p>
      </div>

      {/* ── Section 1: Current AI list ───────────────────────────────────── */}
      <div style={{ marginBottom: 32 }}>
        <div style={sectionHeader}>
          Current AI Coding List
          <span style={countBadge(aiList.length, aiList.length > 0 ? "#1a7a1a" : "#666", aiList.length > 0 ? "#e8f5e9" : "#f0f0f0")}>
            {aiList.length} GL{aiList.length !== 1 ? "s" : ""}
          </span>
        </div>

        {aiList.length === 0 ? (
          <div style={{
            padding: "20px 16px", background: "#fff8e1", border: "1px solid #ffe082",
            borderRadius: 6, fontSize: 13, color: "#7a6000",
          }}>
            No GL accounts in the AI list yet. Add accounts from the section below.
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>GL Number</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Type</th>
                <th style={{ ...thStyle, width: 100 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {aiList
                .sort((a, b) => a.gl_number.localeCompare(b.gl_number))
                .map((acc) => (
                  <tr key={acc.id} style={{ background: "#f0fdf0" }}>
                    <td style={{ ...tdStyle, ...monoStyle }}>{acc.gl_number}</td>
                    <td style={tdStyle}>{acc.description}</td>
                    <td style={tdStyle}>{catBadge(acc.category)}</td>
                    <td style={{ ...tdStyle, fontSize: 12, color: "#666" }}>{acc.account_type}</td>
                    <td style={tdStyle}>
                      <button
                        style={removeBtn(toggling === acc.id)}
                        disabled={toggling === acc.id}
                        onClick={() => handleToggle(acc, false)}
                      >
                        {toggling === acc.id ? "..." : "Remove"}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Section 2: Available GLs to add ─────────────────────────────── */}
      <div>
        <div style={sectionHeader}>
          Add GL Accounts
          <span style={countBadge(available.length)}>
            {available.length} available
          </span>
        </div>

        <input
          style={{
            padding: "8px 12px", border: "1px solid #ddd", borderRadius: 4,
            fontSize: 14, width: 300, marginBottom: 12, outline: "none",
          }}
          placeholder="Search by GL number, description, or category..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {available.length === 0 ? (
          <div style={{
            padding: "20px 16px", background: "#f5f5f5", border: "1px solid #ddd",
            borderRadius: 6, fontSize: 13, color: "#666",
          }}>
            {search
              ? "No GL accounts match your search."
              : "All active GL accounts are already in the AI coding list."}
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>GL Number</th>
                <th style={thStyle}>Description</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Type</th>
                <th style={{ ...thStyle, width: 100 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {available
                .sort((a, b) => a.gl_number.localeCompare(b.gl_number))
                .map((acc) => (
                  <tr
                    key={acc.id}
                    style={{ background: "white" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f5f5"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "white"; }}
                  >
                    <td style={{ ...tdStyle, ...monoStyle }}>{acc.gl_number}</td>
                    <td style={tdStyle}>{acc.description}</td>
                    <td style={tdStyle}>{catBadge(acc.category)}</td>
                    <td style={{ ...tdStyle, fontSize: 12, color: "#666" }}>{acc.account_type}</td>
                    <td style={tdStyle}>
                      <button
                        style={addBtn(toggling === acc.id)}
                        disabled={toggling === acc.id}
                        onClick={() => handleToggle(acc, true)}
                      >
                        {toggling === acc.id ? "..." : "+ Add"}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AIGLList;
